import { mapState } from 'vuex';
import { Options, Vue } from 'vue-class-component';
import {
  AddressingType,
  ApartmentDTO,
  BuildingDTO,
  EntrancePlantDetailDTO,
  FloorDTO,
  RiserDTO,
  RiserInterfaceMode,
} from '@/../client-generator/generated-clients/plants';
import ApartmentItem from '../ApartmentItem/ApartmentItem.vue';
import DialogAddEditApartment from '../DialogAddEditApartment/DialogAddEditApartment.vue';
import plantsClient from '@/core/services/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import DialogConfirmDelete from '@/views/components/DialogConfirmDelete/DialogConfirmDelete.vue';
import { getInterfaceModeLabel, RiserInterfaceFloorApartments, loadRisersFloorsApartments, EMPTY_GUID } from '@/core/utils/EntrancePlant/entrance-plant.utils';
import { NamingService } from '@/core/services/naming.service';
import debounce from 'lodash.debounce';
import { NAME_VALIDATION_DEBOUNCE_TIME } from '@/core/utils/utils';
import DialogEditRiser from '../DialogAddEditRiser/DialogEditRiser.vue';
import DialogAddFloor from './DialogAddFloor/DialogAddFloor.vue';

@Options({
  name: 'EntrancePlantStructureRiser',
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  components: {
    ApartmentItem,
    DialogAddEditApartment,
    DialogEditRiser,
    DialogAddFloor,
    DialogConfirmDelete,
  },
  emits: ['update-list'],
})
export default class EntrancePlantStructureRiser extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  isVisibleDeleteApartmentConfirm = false;
  isVisibleDeleteRiserConfirm = false;
  apartmentToEdit: ApartmentDTO | null = null;
  apartmentToDeleteId: string | null = null;
  riserToEdit: RiserDTO | null = null;
  isVisibleAddEditApartment = false;
  isVisibleEditRiser = false;
  isVisibleAddFloor = false;
  isValidName = true;

  EmptyGuid = EMPTY_GUID;

  AddressingType = AddressingType;

  async created() {
    this.verifyValidityOfName = debounce(
      this.verifyValidityOfName,
      NAME_VALIDATION_DEBOUNCE_TIME + 1000
    );
  }

  mounted() {
  }

  get backLink() {
    return {
      name: 'page-entranceplant-structure-building',
      params: { buildingId: this.$route.params.buildingId },
    };
  }

  get building() {
    return this.currentEntrancePlant.buildings!.find(
      (plantBuilding) =>
        plantBuilding.buildingId === this.$route.params.buildingId.toString()
    ) ?? { name: 'building' } as BuildingDTO;
  }

  get riser() {
    if (this.$route.params.riserId.toString() === EMPTY_GUID) {
      return new RiserDTO({ riserId: EMPTY_GUID, name: this.$t('AppResourceEP.Backbone') ?? '' });
    }
    return this.building?.risers?.find(
      (buildingRiser) =>
        buildingRiser.riserId === this.$route.params.riserId.toString()
    );
  }

  get apartments() {
    return this.riserStructure.reduce((acc, tab) => acc.concat(...tab.floors.flatMap(floor => floor.apartments ?? [])), [] as ApartmentDTO[]);
  }

  get riserStructure(): RiserInterfaceFloorApartments[] {
    if (!this.building || (this.building?.risers === undefined && !this.currentEntrancePlant.hasRiserInterface)) return [];
    const buildingStructure = loadRisersFloorsApartments(this.building, this.currentEntrancePlant.hasRiserInterface);
    return buildingStructure.find(struct => struct.riser.riserId === this.riser?.riserId)?.interfaceTabs ?? [];
  }

  get apartmentsAvailable() {
    return this.currentEntrancePlant.buildings!.reduce(
      (acc, building) => acc + (building.apartments?.length ?? 0),
      0
    ) < 3999;
  }

  get noBackboneInterface() {
    return this.currentEntrancePlant.buildings?.every(building => building.backboneInterface == null);
  }

  get isBrfa() {
    return this.currentEntrancePlant.addressingType === AddressingType.BuildingRiserFloorApartment;
  }

  get noFloors() {
    if (this.currentEntrancePlant.hasRiserInterface) {
      const validfloors = this.riserStructure.flatMap(tab => tab.floors).filter(floor => floor.floor.floorId !== EMPTY_GUID);
      return validfloors.length === 0;
    }
    else {
      return this.building?.floors === undefined || this.building.floors.length === 0;
    }
  }

  async verifyValidityOfName(newName: string, floor: FloorDTO) {
    if (newName === floor.name) return;
    const validName = NamingService.isValidEntityTypeName(newName);

    if (!validName) {
      this.isValidName = validName;
    } else {
      this.isValidName = true;
      await this.updateFloorName(newName, floor);
    }
  }

  async updateFloorName(newName: string, floor: FloorDTO) {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      floor.name = newName;
      await plantsClient.floor(this.currentEntrancePlant.desktopId, floor)
      this.$toast.add({
        severity: 'success',
        summary: this.$t('entrancePlant.floorEditedBanner'),
        life: 3000,
      });
      NamingService.forcefullyRenameFirstEntityWithinCachedOnes(
        floor.name!,
        newName
      );
    } catch (e) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('entrancePlant.floorInlineNamingError'),
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
      this.$emitter.emit('update-list');
    }
  }

  toggleAddApartment() {
    this.apartmentToEdit = null;
    this.isVisibleAddEditApartment = true;
  }

  toggleEditApartment(apartment: ApartmentDTO) {
    this.apartmentToEdit = apartment;
    this.isVisibleAddEditApartment = true;
  }

  hideAddEditApartment() {
    this.isVisibleAddEditApartment = false;
  }

  toggleDeleteApartment(apartmentId: string) {
    this.apartmentToDeleteId = apartmentId ?? null;
    this.isVisibleDeleteApartmentConfirm = !this.isVisibleDeleteApartmentConfirm;
  }

  toggleEditRiser() {
    this.isVisibleEditRiser = !this.isVisibleEditRiser;
  }

  toggleAddFloor() {
    this.isVisibleAddFloor = !this.isVisibleAddFloor;
  }

  toggleDeleteRiserConfirm() {
    this.isVisibleDeleteRiserConfirm = !this.isVisibleDeleteRiserConfirm;
  }

  handleUpdateList() {
    this.$emit('update-list');
  }

  async handleDeleteRiser() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      await plantsClient.riserDELETE(
        this.currentEntrancePlant.desktopId,
        this.riser?.riserId
      )
      this.$router.push(this.backLink);
      this.$emit('update-list');
      
      setTimeout(() => {
        this.$toast.add({
          severity: 'success',
          summary: this.$t('entrancePlant.riserBannerDeleted'),
          closable: false,
          life: 3000,
        });
      }, 1500);
    } catch {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  async handleDeleteApartment() {
    if (this.currentEntrancePlant.desktopId === undefined || this.apartmentToDeleteId === null) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      await plantsClient.apartmentDELETE(
        this.currentEntrancePlant.desktopId,
        this.apartmentToDeleteId
      )
      this.$emit('update-list');
      setTimeout(() => {
        this.$toast.add({
          severity: 'success',
          summary: this.$t('entrancePlant.apartmentBannerDeleted'),
          closable: false,
          life: 3000,
        });
      }, 1500);
    } catch {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('general.operationError'),
        closable: true,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  getInterfaceModeLabelAndDesc(interfaceTab: RiserInterfaceFloorApartments, index: number)
  {
    const interfaceMode = interfaceTab.interface.mode;
    const totInterfaces = this.riserStructure.length;
    let result = getInterfaceModeLabel(interfaceMode);
    
    if (interfaceMode === RiserInterfaceMode.Mode7ExtendedRiserExpansion && totInterfaces > 2) {
      const orderValue = index === 1 ? this.$t("AppResourceEP.First") : this.$t("AppResourceEP.Second");
      result += ` (${orderValue})`;
    }
    
    return result;
  }

}
